import { useState, useRef, useEffect } from 'react'

interface VideoPlayerProps {
  src: string
  title?: string
  className?: string
  autoPlay?: boolean
  controls?: boolean
  onError?: (error: string) => void
  onLoadStart?: () => void
  onLoadedData?: () => void
  onProgress?: (progress: number) => void
}

export default function VideoPlayer({
  src,
  title = 'Video',
  className = '',
  autoPlay = false,
  controls = true,
  onError,
  onLoadStart,
  onLoadedData,
  onProgress
}: VideoPlayerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [canPlay, setCanPlay] = useState(false)
  const [bufferedProgress, setBufferedProgress] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleLoadStart = () => {
    setIsLoading(true)
    setHasError(false)
    setCanPlay(false)
    onLoadStart?.()
  }

  const handleLoadedData = () => {
    setIsLoading(false)
    onLoadedData?.()
  }

  const handleCanPlay = () => {
    setIsLoading(false)
    setCanPlay(true)
  }

  const handleCanPlayThrough = () => {
    setIsLoading(false)
    setCanPlay(true)
  }

  const handleProgress = () => {
    const video = videoRef.current
    if (video && video.buffered.length > 0) {
      const buffered = video.buffered.end(0)
      const duration = video.duration
      if (duration > 0) {
        const progress = (buffered / duration) * 100
        setBufferedProgress(progress)
        onProgress?.(progress)
      }
    }
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)
    const error = 'Erro ao carregar o vídeo. Verifique se o arquivo existe e as permissões estão corretas.'
    setErrorMessage(error)
    onError?.(error)
  }

  const handleWaiting = () => {
    setIsLoading(true)
  }

  const handlePlaying = () => {
    setIsLoading(false)
  }

  const handleRetry = () => {
    setHasError(false)
    setIsLoading(true)
    setCanPlay(false)
    if (videoRef.current) {
      videoRef.current.load()
    }
  }

  // Reinicializar player quando a URL mudar
  useEffect(() => {
    if (videoRef.current && src) {
      setIsLoading(true)
      setHasError(false)
      setCanPlay(false)
      videoRef.current.load()
    }
  }, [src])

  if (hasError) {
    return (
      <div className={`relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden ${className}`}>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <svg 
              className="w-8 h-8 text-red-600 dark:text-red-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Erro ao carregar vídeo
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {errorMessage}
          </p>
          <button
            onClick={handleRetry}
            className="btn-primary px-4 py-2 text-sm"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {canPlay ? 'Carregando...' : 'Preparando vídeo...'}
            </p>
            {bufferedProgress > 0 && (
              <div className="w-32 bg-gray-300 dark:bg-gray-600 rounded-full h-1 mt-2">
                <div 
                  className="bg-blue-600 dark:bg-blue-400 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${bufferedProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <video
        ref={videoRef}
        src={src}
        title={title}
        controls={controls}
        autoPlay={autoPlay}
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        onCanPlay={handleCanPlay}
        onCanPlayThrough={handleCanPlayThrough}
        onProgress={handleProgress}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onError={handleError}
        className="w-full h-full object-contain"
        preload="auto"
        crossOrigin="anonymous"
        playsInline
      >
        <p className="text-gray-600 dark:text-gray-300 p-4">
          Seu navegador não suporta o elemento de vídeo.
        </p>
      </video>
    </div>
  )
}
